import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from './ui/button';
import { useBrand } from '../context/BrandContext';

export default function BrandHeader() {
  const [open, setOpen] = useState(false);
  const { brand, mode } = useBrand();
  const loc = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  if (!brand) return null;

  const primary = brand.primary_color || '#0d63b0';
  // Base path for links: if we're on custom domain, use /... else use /b/:slug/...
  const basePath = mode === 'branded-host' ? '' : `/b/${brand.slug}`;

  const isActive = (to) => {
    if (to === basePath || to === basePath + '/') {
      const p = mode === 'branded-host' ? '/' : `/b/${brand.slug}`;
      return loc.pathname === p || loc.pathname === `${p}/`;
    }
    return loc.pathname.startsWith(to);
  };

  const homeHref = basePath || '/';
  const newPushHref = `${basePath}/p/new`;
  const isOnHome = isActive(basePath);

  // Home click: if already on landing page, smooth-scroll to top. Otherwise navigate to landing.
  const onHomeClick = (e) => {
    if (isOnHome) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200/80 bg-white/85 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={homeHref} className="flex items-center gap-3 group">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.name} className="h-9 w-auto max-w-[180px] object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          ) : (
            <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}>{brand.name?.[0]?.toUpperCase()}</div>
          )}
          <div className="leading-tight border-l border-slate-200 pl-3 hidden sm:block">
            <div className="font-bold text-slate-900 text-[15px]">{brand.name}</div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold" style={{ color: primary }}>Secure Push</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <Link to={homeHref} onClick={onHomeClick} data-testid="brand-nav-home" className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(basePath) ? 'bg-blue-50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} style={isActive(basePath) ? { color: primary } : {}}>Home</Link>
          <Link to={newPushHref} className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(newPushHref) ? 'bg-blue-50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`} style={isActive(newPushHref) ? { color: primary } : {}}>Nuovo Push</Link>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button asChild className="text-white shadow-sm" style={{ backgroundColor: primary }}>
            <Link to={newPushHref}>Crea Push</Link>
          </Button>
        </div>

        <button className="md:hidden p-2 text-slate-700" onClick={() => setOpen(!open)} aria-label="menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white">
          <div className="px-4 py-3 space-y-1">
            <Link to={homeHref} onClick={onHomeClick} data-testid="brand-nav-home-mobile" className="block px-3 py-2 rounded-md text-sm font-medium text-slate-700">Home</Link>
            <Link to={newPushHref} onClick={() => setOpen(false)} className="block mt-2 text-center text-white rounded-md px-3 py-2 text-sm font-semibold" style={{ backgroundColor: primary }}>Crea Push</Link>
          </div>
        </div>
      )}
    </header>
  );
}
